import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LoginPage } from '@/pages/LoginPage'
import { useAuth } from '@/hooks/useAuth'
import { CREW_PATH } from '@/lib/crewRouting'
import { FieldRedirect } from '@/crew/FieldRedirect'
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary'
import { StaleBundlePrompt } from '@/components/shared/StaleBundlePrompt'
import { REDIRECT_STATE_KEY, captureRedirectTarget } from '@/lib/redirectTarget'

/**
 * Every route below the login page is code-split.
 *
 * This is not incidental tidiness — it is what makes the crew shell viable on a
 * phone. With static imports the whole app (all pages plus the maps, table,
 * dnd and markdown vendors) lands in one ~347 kB gzip chunk that a field crew
 * would download to see a clock button. Lazy routes let /crew pull only React,
 * the api client and its own components; the desktop pages arrive when a
 * desktop user asks for them.
 */
const AppShell = lazy(() =>
  import('@/components/layout/AppShell').then((m) => ({ default: m.AppShell })),
)
const CrewShell = lazy(() =>
  import('@/crew/CrewShell').then((m) => ({ default: m.CrewShell })),
)

const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const JobsPage = lazy(() => import('@/pages/JobsPage').then((m) => ({ default: m.JobsPage })))
const JobDetailPage = lazy(() =>
  import('@/pages/JobDetailPage').then((m) => ({ default: m.JobDetailPage })),
)
const ClientsPage = lazy(() =>
  import('@/pages/ClientsPage').then((m) => ({ default: m.ClientsPage })),
)
const ClientDetailPage = lazy(() =>
  import('@/pages/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })),
)
const QuotesPage = lazy(() =>
  import('@/pages/QuotesPage').then((m) => ({ default: m.QuotesPage })),
)
const QuoteDetailPage = lazy(() =>
  import('@/pages/QuoteDetailPage').then((m) => ({ default: m.QuoteDetailPage })),
)
const ReportsPage = lazy(() =>
  import('@/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
)
const MapPage = lazy(() => import('@/pages/MapPage').then((m) => ({ default: m.MapPage })))
const HoldsPage = lazy(() => import('@/pages/HoldsPage').then((m) => ({ default: m.HoldsPage })))
const TimeSheetPage = lazy(() =>
  import('@/pages/TimeSheetPage').then((m) => ({ default: m.TimeSheetPage })),
)
const PayrollRollupPage = lazy(() =>
  import('@/pages/PayrollRollupPage').then((m) => ({ default: m.PayrollRollupPage })),
)
const PayrollEmployeePage = lazy(() =>
  import('@/pages/PayrollEmployeePage').then((m) => ({ default: m.PayrollEmployeePage })),
)
const PayrollDiffPage = lazy(() =>
  import('@/pages/PayrollDiffPage').then((m) => ({ default: m.PayrollDiffPage })),
)
const PayrollRollupPrintPage = lazy(() =>
  import('@/pages/PayrollRollupPrintPage').then((m) => ({ default: m.PayrollRollupPrintPage })),
)
const PayrollEmployeePrintPage = lazy(() =>
  import('@/pages/PayrollEmployeePrintPage').then((m) => ({
    default: m.PayrollEmployeePrintPage,
  })),
)
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const BoardsPage = lazy(() => import('@/pages/BoardsPage').then((m) => ({ default: m.BoardsPage })))
const JobPipelinePage = lazy(() =>
  import('@/pages/JobPipelinePage').then((m) => ({ default: m.JobPipelinePage })),
)
const QuotePipelinePage = lazy(() =>
  import('@/pages/QuotePipelinePage').then((m) => ({ default: m.QuotePipelinePage })),
)
const DrafterPipelinePage = lazy(() =>
  import('@/pages/DrafterPipelinePage').then((m) => ({ default: m.DrafterPipelinePage })),
)
const CrewSchedulingPage = lazy(() =>
  import('@/pages/CrewSchedulingPage').then((m) => ({ default: m.CrewSchedulingPage })),
)
const TemplateListPage = lazy(() =>
  import('@/pages/TemplateListPage').then((m) => ({ default: m.TemplateListPage })),
)
const TemplateEditorPage = lazy(() =>
  import('@/pages/TemplateEditorPage').then((m) => ({ default: m.TemplateEditorPage })),
)
const QCTemplateListPage = lazy(() =>
  import('@/pages/QCTemplateListPage').then((m) => ({ default: m.QCTemplateListPage })),
)
const QCTemplateEditorPage = lazy(() =>
  import('@/pages/QCTemplateEditorPage').then((m) => ({ default: m.QCTemplateEditorPage })),
)
const CreateQuotePage = lazy(() =>
  import('@/pages/CreateQuotePage').then((m) => ({ default: m.CreateQuotePage })),
)
const CreateClientPage = lazy(() =>
  import('@/pages/CreateClientPage').then((m) => ({ default: m.CreateClientPage })),
)
const PaymentBatchesPage = lazy(() =>
  import('@/pages/PaymentBatchesPage').then((m) => ({ default: m.PaymentBatchesPage })),
)
const PaymentBatchWorksheetPage = lazy(() =>
  import('@/pages/PaymentBatchWorksheetPage').then((m) => ({
    default: m.PaymentBatchWorksheetPage,
  })),
)
const PaymentFinderPage = lazy(() =>
  import('@/pages/PaymentFinderPage').then((m) => ({ default: m.PaymentFinderPage })),
)
const AdvancedSearchPage = lazy(() =>
  import('@/pages/AdvancedSearchPage').then((m) => ({ default: m.AdvancedSearchPage })),
)

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="text-sm text-gray-400">Loading...</div>
    </div>
  )
}

/**
 * Crew shell guard — same auth contract as AppShell, none of its chrome.
 *
 * Exported for the redirect tests: the capture below is half of the deep-link
 * contract, and asserting it through the whole <App /> tree would mean booting
 * every lazy route to check one <Navigate>.
 */
export function CrewRoute() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return <RouteFallback />
  if (!isAuthenticated) {
    // Remember where they were headed so login can finish the journey. See
    // lib/redirectTarget.ts for why this rides in router state, not the URL.
    return (
      <Navigate
        to="/login"
        replace
        state={{ [REDIRECT_STATE_KEY]: captureRedirectTarget(location) }}
      />
    )
  }
  return <CrewShell />
}

/**
 * Print-view guard (PAYROLL-PRINT-1) — the same auth contract as AppShell and
 * CrewRoute, none of the chrome: a printout is the document alone, and
 * AppShell's `h-screen overflow-hidden` frame would cut it off at one screen.
 *
 * A deliberate COPY of CrewRoute's check above, not a shared extraction: the
 * existing tests pin only CrewRoute's unauthenticated capture, not its loading
 * or signed-in branches, so lifting it was not provably behaviour-neutral (B,
 * STOP-1 GO). Keep the two in step. Each print page then runs its own page's
 * guard predicate — this gate is sign-in only.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return <RouteFallback />
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ [REDIRECT_STATE_KEY]: captureRedirectTarget(location) }}
      />
    )
  }
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      {/* Outside the boundary, so it still shows when the boundary has
          replaced the routes after a chunk from an older deploy failed. */}
      <StaleBundlePrompt />
      {/* A failed lazy chunk or a render throw must never leave a blank page —
          especially not on a phone in the field, where there is no console to
          check and no obvious way back. */}
      <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Crew shell — deliberately OUTSIDE AppShell so the phone never
                downloads the nav bar, chat sidebar or their dependencies. */}
            <Route path={CREW_PATH} element={<CrewRoute />} />

            {/* Payroll print views — outside AppShell, so the printout is the
                document alone. Each page runs its screen page's own guard. */}
            <Route
              path="/payroll/print"
              element={
                <AuthGate>
                  <PayrollRollupPrintPage />
                </AuthGate>
              }
            />
            <Route
              path="/payroll/employee/:employeeId/print"
              element={
                <AuthGate>
                  <PayrollEmployeePrintPage />
                </AuthGate>
              }
            />

            {/* Protected layout — AppShell redirects to /login if not authenticated */}
            <Route element={<AppShell />}>
              <Route
                index
                element={
                  <FieldRedirect>
                    <DashboardPage />
                  </FieldRedirect>
                }
              />
              <Route path="/jobs" element={<JobsPage />} />
              {/* ADV-SEARCH-1 — Stars' Advanced Search; linked from the Jobs
                  list. A static segment, so it outranks /jobs/:id. */}
              <Route path="/jobs/search" element={<AdvancedSearchPage />} />
              {/* /jobs/new is retired (Queue 3). Every job is born from a
                  quote — the creation surface at /quotes/new is the only
                  door, and its Create & Start Job commit is the fast lane. */}
              <Route path="/jobs/:id" element={<JobDetailPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/new" element={<CreateClientPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              <Route path="/quotes" element={<QuotesPage />} />
              <Route path="/quotes/new" element={<CreateQuotePage />} />
              <Route path="/quotes/:id" element={<QuoteDetailPage />} />
              {/* Payments — the subsystem entry. Batches now, AR at the arc. */}
              <Route path="/payments" element={<PaymentBatchesPage />} />
              {/* PAY-FINDER-1 — pricing-gated, reached from the Payments
                  header and the Reports card; the nav is unchanged. */}
              <Route path="/payments/find" element={<PaymentFinderPage />} />
              <Route
                path="/payments/batches/:id"
                element={<PaymentBatchWorksheetPage />}
              />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/holds" element={<HoldsPage />} />
              <Route path="/timesheet" element={<TimeSheetPage />} />
              <Route path="/payroll" element={<PayrollRollupPage />} />
              <Route path="/payroll/employee/:employeeId" element={<PayrollEmployeePage />} />
              <Route path="/payroll/diff" element={<PayrollDiffPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/boards" element={<BoardsPage />} />
              <Route path="/boards/jobs" element={<JobPipelinePage />} />
              <Route path="/boards/quotes" element={<QuotePipelinePage />} />
              <Route path="/boards/drafters" element={<DrafterPipelinePage />} />
              <Route path="/boards/crew" element={<CrewSchedulingPage />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/settings/templates" element={<TemplateListPage />} />
              <Route path="/settings/templates/:id" element={<TemplateEditorPage />} />
              <Route path="/settings/qc-templates" element={<QCTemplateListPage />} />
              <Route path="/settings/qc-templates/:id" element={<QCTemplateEditorPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </BrowserRouter>
  )
}
